import React, { useMemo } from "react";
import {
    ResponsiveContainer,
    BarChart,
    Bar,
    XAxis,
    YAxis,
    CartesianGrid,
    Tooltip,
    LabelList,
    Legend,
} from "recharts";
import type { NameType, ValueType } from "recharts/types/component/DefaultTooltipContent";

export type BackendItemMovements = {
    id: string;
    name: string;
    totalIn: string | number;
    totalOut: string | number;
};

export type MovementsProps = {
    title?: string;
    periodLabel: string;
    items?: BackendItemMovements[];
    loading?: boolean;
    error?: string | null;
    height?: number;
    className?: string;
    maxRows?: number;
};

const fmtU = (n: number) => new Intl.NumberFormat("es-ES").format(n);

export const StockMovementsPerProductCard: React.FC<MovementsProps> = ({
                                                                           title = "Movimientos de stock por producto",
                                                                           periodLabel,
                                                                           items = [],
                                                                           loading = false,
                                                                           error = null,
                                                                           height = 320,
                                                                           className = "",
                                                                           maxRows = 6,
                                                                       }) => {
    const data = useMemo(() => {
        const parsed = (items || []).map((it) => ({
            id: it.id,
            name: it.name,
            in: Number.parseFloat(String(it.totalIn)) || 0,
            out: Number.parseFloat(String(it.totalOut)) || 0,
        }));
        // ordena por (in + out) desc y corta a top N
        return parsed.sort((a, b) => (b.in + b.out) - (a.in + a.out)).slice(0, maxRows);
    }, [items, maxRows]);

    const noData = !loading && !error && data.length === 0;

    return (
        <div className={`rounded-2xl border bg-white shadow-sm ${className}`} style={{ minHeight: height }}>
            <div className="p-4 flex items-center justify-between">
                <h3 className="font-semibold">{title}</h3>
                <div className="text-sm text-gray-600">{periodLabel}</div>
            </div>

            <div className="px-4 pb-6 relative">
                {loading ? (
                    <div className="p-6 text-sm text-gray-500">Cargando movimientos…</div>
                ) : error ? (
                    <div className="p-6 text-sm text-rose-600">{error}</div>
                ) : noData ? (
                    <div className="p-6 text-sm text-gray-500">No hay datos para el periodo seleccionado.</div>
                ) : (
                    <div style={{ height: height - 90 }}>
                        <ResponsiveContainer width="100%" height="100%">
                            <BarChart
                                data={data}
                                layout="vertical"
                                margin={{ top: 8, right: 16, bottom: 0, left: 8 }}
                                barCategoryGap={10}
                            >
                                <CartesianGrid strokeDasharray="3 3" />
                                <XAxis
                                    type="number"
                                    tickLine={false}
                                    axisLine={false}
                                    tick={{ fontSize: 11 }}
                                    tickFormatter={(v) => new Intl.NumberFormat("es-ES").format(Number(v))}
                                />
                                <YAxis
                                    type="category"
                                    dataKey="name"
                                    width={220}
                                    tickLine={false}
                                    axisLine={false}
                                    tick={{ fontSize: 12 }}
                                />
                                <Tooltip
                                    cursor={{ opacity: 0.08 }}
                                    formatter={(value: ValueType, name: NameType) => {
                                        const v = Number(value);
                                        console.log(name, value, v);
                                        const label = name === "Entradas" ? "Entradas" : "Salidas";
                                        return [fmtU(v), label as NameType];
                                    }}
                                />
                                <Legend wrapperStyle={{ fontSize: 12 }} />
                                <Bar dataKey="in" name="Entradas" radius={[8, 8, 8, 8]} fill="#10b981">
                                    <LabelList dataKey="in" position="right" formatter={(v: any) => fmtU(Number(v))} className="text-[11px] fill-gray-700" />
                                </Bar>
                                <Bar dataKey="out" name="Salidas" radius={[8, 8, 8, 8]} fill="#ef4444">
                                    <LabelList dataKey="out" position="right" formatter={(v: any) => fmtU(Number(v))} className="text-[11px] fill-gray-700" />
                                </Bar>
                            </BarChart>
                        </ResponsiveContainer>
                    </div>
                )}
            </div>
        </div>
    );
};
